import { ErpDiv } from './ErpHtml';
import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { useTranslation } from 'react-i18next';
import { LoaderCircle } from 'lucide-react';
import { useDialogFocusRestore } from '@/hooks/useDialogFocusRestore';
import { translateDisplayLabel } from '@/utils/translateDisplayLabel';

export interface ErpModalProps {
  modelValue?: boolean;
  isOpen?: boolean;
  show?: boolean;
  toggle?: () => void;
  className?: string;
  onUpdateModelValue?: (v: boolean) => void;
  title?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'fullscreen' | string;
  okTitle?: string;
  cancelTitle?: string;
  okOnly?: boolean;
  okDisabled?: boolean;
  hideHeader?: boolean;
  hideFooter?: boolean;
  noFooter?: boolean;
  hideHeaderClose?: boolean;
  noCloseOnBackdrop?: boolean;
  noCloseOnEsc?: boolean;
  centered?: boolean;
  scrollable?: boolean;
  lazy?: boolean;
  busy?: boolean;
  modalClass?: string;
  dialogClass?: string;
  contentClass?: string;
  headerClass?: string;
  bodyClass?: string;
  footerClass?: string;
  titleClass?: string;
  
  onOk?: (e: React.MouseEvent) => void | boolean | Promise<void | boolean>;
  onCancel?: (e: React.MouseEvent) => void;
  onHide?: () => void;
  onHidden?: () => void;
  onShow?: () => void;
  onShown?: () => void;
  onClose?: () => void;

  headerSlot?: React.ReactNode | ((props: any) => React.ReactNode);
  modalTitleSlot?: React.ReactNode;
  footerSlot?: React.ReactNode | ((props: any) => React.ReactNode);
  children?: React.ReactNode | ((props: any) => React.ReactNode);
  header?: React.ReactNode;
  footer?: React.ReactNode;
  
  // Legacy props kept to avoid TS errors in consuming components
  id?: string;
  zIndex?: number;
  inModalHost?: boolean;
}

export const ErpModal: React.FC<ErpModalProps> = ({
  modelValue = false,
  onUpdateModelValue,
  title,
  size = 'md',
  okTitle,
  cancelTitle,
  okOnly = false,
  okDisabled = false,
  hideHeader = false,
  hideFooter = false,
  noFooter = false,
  hideHeaderClose = false,
  noCloseOnBackdrop = false,
  noCloseOnEsc = false,
  scrollable: _scrollable = false,
  busy = false,
  modalClass,
  dialogClass,
  contentClass,
  headerClass,
  bodyClass,
  footerClass,
  titleClass,
  onOk,
  onCancel,
  onHide,
  onClose,
  toggle,
  headerSlot,
  modalTitleSlot,
  footerSlot,
  children,
  isOpen: _isOpen,
  show,
  header,
  footer
}) => {
  const { t } = useTranslation();
  const { handleApiError } = useErrorHandler();
  const focusRestore = useDialogFocusRestore();
  const isModalOpen = _isOpen ?? show ?? modelValue;

  const handleOpenChange = (open: boolean) => {
    if (busy) return;
    if (!open) {
      onClose?.();
      onHide?.();
      if (!onClose && !onHide) toggle?.();
      onUpdateModelValue?.(false);
    }
  };

  const hide = () => handleOpenChange(false);

  const handleOk = async (e: React.MouseEvent) => {
    if (busy || okDisabled) return;
    try {
      const result = await onOk?.(e);
      if (result === false) return;
      hide();
    } catch (error) {
      // keep modal open when onOk fails (form validation / API error),
      // but surface the failure instead of swallowing it silently
      console.error('ErpModal onOk failed:', error);
      handleApiError(error ?? {});
    }
  };

  const handleCancel = (e: React.MouseEvent) => {
    if (busy) return;
    onCancel?.(e);
    hide();
  };

  let sizeClass = 'sm:max-w-md';
  if (size === 'sm') sizeClass = 'sm:max-w-sm';
  if (size === 'lg') sizeClass = 'sm:max-w-lg';
  if (size === 'xl') sizeClass = 'sm:max-w-2xl';
  if (size === 'fullscreen') sizeClass = 'w-screen h-dvh max-w-none sm:max-w-none m-0 rounded-none';

  return (
    <Dialog open={isModalOpen} onOpenChange={handleOpenChange}>
      <DialogContent 
        {...focusRestore}
        className={`${sizeClass} flex min-w-0 flex-col gap-3 overflow-hidden ${size === 'fullscreen' ? '' : 'max-h-[calc(100dvh-2rem)]'} ${modalClass || ''} ${dialogClass || ''} ${contentClass || ''}`}
        showCloseButton={!hideHeaderClose}
        onInteractOutside={(e) => {
          if (noCloseOnBackdrop) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          // React-select handles Escape in its input after Radix's document
          // listener. Let the open menu consume the first Escape.
          const menuOpen = e.target instanceof Element && e.target.closest('[role="combobox"][aria-expanded="true"]');
          if (noCloseOnEsc || menuOpen) e.preventDefault();
        }}
      >
        {!hideHeader && (
          <DialogHeader className={`min-w-0 shrink-0 pe-10 ${headerClass || ''}`}>
            {headerSlot || header ? (
              typeof headerSlot === 'function' ? headerSlot({ close: hide, hide }) : (headerSlot || header)
            ) : (
              (title || modalTitleSlot) && (
                <DialogTitle className={`break-words leading-snug ${titleClass || ''}`}>
                  {modalTitleSlot || (typeof title === 'string' ? translateDisplayLabel(t, title) : title)}
                </DialogTitle>
              )
            )}
          </DialogHeader>
        )}

        <ErpDiv className={`relative min-h-0 min-w-0 max-w-full flex-1 overflow-y-auto py-2 ${bodyClass || ''}`} aria-busy={busy}>
          {busy && (
            <ErpDiv className="absolute inset-0 z-10 flex items-center justify-center bg-background/50 backdrop-blur-[1px] rounded-md">
              <LoaderCircle className="animate-spin text-primary" size={32} />
            </ErpDiv>
          )}
          {typeof children === 'function' ? children({ hide, ok: handleOk, cancel: handleCancel, close: hide }) : children}
        </ErpDiv>

        {!(hideFooter || noFooter) && (
          <DialogFooter className={`shrink-0 sm:flex-wrap ${footerClass || ''}`}>
            {footerSlot || footer ? (
              typeof footerSlot === 'function' ? footerSlot({ ok: handleOk, cancel: handleCancel, hide, close: hide }) : (footerSlot || footer)
            ) : (
              <>
                {!okOnly && (
                  <Button className="min-h-10 max-sm:min-h-11"
                    variant="outline"
                    disabled={busy}
                    onClick={handleCancel}
                  >
                    {cancelTitle ? translateDisplayLabel(t, cancelTitle) : t('Cancel')}
                  </Button>
                )}
                <Button
                  variant="default"
                  disabled={busy || okDisabled}
                  onClick={handleOk}
                >
                  {busy ? <LoaderCircle size={16} className="animate-spin" /> : null}
                  {okTitle ? translateDisplayLabel(t, okTitle) : t('OK')}
                </Button>
              </>
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ErpModal;
